import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPostJson, apiPutJson, ApiError } from '../api/client';
import { BUSINESSES } from '../domain/businesses';
import { todayIso } from '../lib/format';

/** Add/edit a staff member. Ports frmAddStaff. */

interface StaffDetail {
  staff: {
    id: number;
    emri: string;
    mbiemri: string;
    roli: string | null;
    biznesiID: number;
    birthday: string | null;
    email: string | null;
    phone: string | null;
    startDate: string | null;
    rPara: string;
    rDyta: string;
    monthlySalary: number;
  };
}

/** Pay days of the month, 1-31. The columns are varchar(3). */
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1));

export default function StaffModal({
  staffId,
  onClose,
  onSaved,
}: {
  staffId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = staffId !== null;

  const [emri, setEmri] = useState('');
  const [mbiemri, setMbiemri] = useState('');
  const [roli, setRoli] = useState('');
  const [biznesiId, setBiznesiId] = useState<number>(1);
  const [birthday, setBirthday] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [startDate, setStartDate] = useState(todayIso);
  const [rPara, setRPara] = useState('15');
  const [rDyta, setRDyta] = useState('30');
  const [monthlySalary, setMonthlySalary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['staff-detail', staffId],
    queryFn: () => apiGet<StaffDetail>(`/stafi/${staffId}`),
    enabled: isEdit,
  });

  useEffect(() => {
    const s = detailQuery.data?.staff;
    if (!s) return;
    setEmri(s.emri ?? '');
    setMbiemri(s.mbiemri ?? '');
    setRoli(s.roli ?? '');
    setBiznesiId(s.biznesiID);
    setBirthday(s.birthday?.slice(0, 10) ?? '');
    setEmail(s.email ?? '');
    setPhone(s.phone ?? '');
    setStartDate(s.startDate?.slice(0, 10) ?? '');
    setRPara(s.rPara ?? '15');
    setRDyta(s.rDyta ?? '30');
    setMonthlySalary(String(s.monthlySalary ?? ''));
  }, [detailQuery.data]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!emri.trim()) return setError('Ju lutem vendosni emrin.');
    if (!mbiemri.trim()) return setError('Ju lutem vendosni mbiemrin.');
    const salary = Number(monthlySalary);
    if (monthlySalary.trim() === '' || !Number.isFinite(salary) || salary < 0) {
      return setError('Ju lutem vendosni një shumë të vlefshme për rrogën mujore.');
    }

    const body = {
      emri: emri.trim(),
      mbiemri: mbiemri.trim(),
      roli: roli.trim() || undefined,
      biznesiId,
      birthday: birthday || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      startDate: startDate || undefined,
      rPara,
      rDyta,
      monthlySalary: salary,
    };

    setSaving(true);
    try {
      if (isEdit) await apiPutJson(`/stafi/${staffId}`, body);
      else await apiPostJson('/stafi', body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ruajtja dështoi. Provoni përsëri.');
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit ? 'Ndrysho punëtorin' : 'Shto punëtor'}
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-emri">
              Emri
            </label>
            <input id="st-emri" value={emri} onChange={(e) => setEmri(e.target.value)} required className={inputClass} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-mbiemri">
              Mbiemri
            </label>
            <input
              id="st-mbiemri"
              value={mbiemri}
              onChange={(e) => setMbiemri(e.target.value)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-roli">
              Roli
            </label>
            <input id="st-roli" value={roli} onChange={(e) => setRoli(e.target.value)} className={inputClass} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-biznesi">
              Biznesi
            </label>
            <select
              id="st-biznesi"
              value={biznesiId}
              onChange={(e) => setBiznesiId(Number(e.target.value))}
              className={`${inputClass} bg-white`}
            >
              {BUSINESSES.map((b, i) => (
                <option key={b.slug} value={i + 1}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-birthday">
              Ditëlindja
            </label>
            <input
              id="st-birthday"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-start">
              Data e fillimit
            </label>
            <input
              id="st-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-email">
              Email
            </label>
            <input
              id="st-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-phone">
              Telefoni
            </label>
            <input id="st-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-para">
              Dita e parë e pagesës
            </label>
            <select
              id="st-para"
              value={rPara}
              onChange={(e) => setRPara(e.target.value)}
              className={`${inputClass} bg-white`}
            >
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-dyta">
              Dita e dytë e pagesës
            </label>
            <select
              id="st-dyta"
              value={rDyta}
              onChange={(e) => setRDyta(e.target.value)}
              className={`${inputClass} bg-white`}
            >
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="st-salary">
              Rroga mujore
            </label>
            <input
              id="st-salary"
              inputMode="decimal"
              value={monthlySalary}
              onChange={(e) => setMonthlySalary(e.target.value.replace(/[^\d.]/g, ''))}
              required
              className={`tnum ${inputClass}`}
            />
            <p className="mt-1 text-xs text-muted">
              Ruhet si rroga e këtij muaji. Historiku i muajve të kaluar nuk ndryshon.
            </p>
          </div>
        </div>

        {error && (
          <div role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Anulo
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </button>
        </div>
      </form>
    </div>
  );
}
